import { ARS_DENOMINATIONS, paymentForAmount, type PaymentLine, type PosSettings } from '@pos/core';

/**
 * Billetes con los que se paga, en centavos: los vigentes de $100 a $20.000 (no hay de $5.000 ni de
 * $50.000). Los de $10, $20 y $50 siguen valiendo pero ya casi no se usan.
 */
const PESO_BILLS = ARS_DENOMINATIONS.filter((c) => c >= 10_000);
const LARGEST_BILL = Math.max(...PESO_BILLS);
const MAX_SUGGESTIONS = 6;

const roundUp = (amount: number, step: number) => Math.ceil(amount / step) * step;

/**
 * Montos sugeridos para "paga con", de menor a mayor. Solo los que alguien entregaría de verdad:
 * - el justo;
 * - el próximo $1.000 o $500 (lo que se junta con billetes de $500, $1.000 y $2.000); por debajo
 *   de $1.000, también el próximo $100;
 * - cada billete que alcanza solo (ej.: $10.000 y $20.000 para $4.725);
 * - por encima del billete más grande, el próximo múltiplo de $10.000 y de $20.000
 *   (ej.: $30.000 y $40.000 para $25.300).
 * No sugiere montos como $6.000 para $4.725 (tres billetes de $2.000): con $5.000 alcanza.
 */
export function cashSuggestions(amount: number): number[] {
  if (amount <= 0) return [];
  const values = new Set<number>([amount, roundUp(amount, 50_000), roundUp(amount, 100_000)]);
  if (amount < 100_000) values.add(roundUp(amount, 10_000));
  for (const bill of PESO_BILLS) if (bill >= amount) values.add(bill);
  if (amount > LARGEST_BILL) {
    values.add(roundUp(amount, 1_000_000));
    values.add(roundUp(amount, LARGEST_BILL));
  }
  return [...values].sort((a, b) => a - b).slice(0, MAX_SUGGESTIONS);
}

/**
 * Efectivo que no alcanza: en «paga con» el cliente da menos de lo que tenía que pagar con ese pago.
 * Devuelve el pago en efectivo por lo que dio (sin vuelto; el descuento por efectivo, si hay, solo
 * sobre esa parte) y la base que falta cubrir, con otro medio o a cuenta corriente. null si no es
 * efectivo, si no dio nada, si alcanza o si solo falta el redondeo.
 */
export function shortCash(line: PaymentLine, settings: PosSettings): { cash: PaymentLine; restBase: number } | null {
  const given = line.tendered;
  if (line.method !== 'cash' || given == null || given <= 0 || given >= line.amount) return null;
  const cash = paymentForAmount('cash', given, line.base, settings);
  const restBase = line.base - cash.base;
  return restBase > 0 ? { cash: { ...cash, tendered: given }, restBase } : null;
}
